import type { JSX } from "react";
import type { ChildrenProp, ClassNameProp, IdProp } from "@charterindex/armoury-www";
import type { PaperTypeMap, SxProps } from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import type { GqlResourceIdentifier } from "../../../resources/resource-selector";
import type { ValueOrI18nKeyProp } from "../../../util/type";
import type { IconComponent } from "../../atoms/icons/icon-component";
import type { PanelActionsProps } from "./panel-actions";
import type { PanelTitleProps } from "./panel-title";
import type { PanelToolbarProps } from "./panel-toolbar";
import { Box, Paper, css, styled } from "@mui/material";
import classNames from "classnames";
import { forwardRef } from "react";
import { resourceProps } from "../../../resources/resource-selector";
import { CentredSpinner } from "../../atoms/centred-spinner";
import { PanelActions } from "./panel-actions";
import { PanelTitle } from "./panel-title";
import { PanelToolbar } from "./panel-toolbar";


const PanelRoot = styled(Paper)(({ theme }) => css`
	position: relative;
	overflow: hidden;

	display: grid;
	grid-template-areas: 'header' 'content' 'footer';
	grid-template-rows: auto 1fr auto;
	z-index: 0;

	&.dark {
		--panel-header-bg: ${theme.palette.dark.light};
		--panel-header-fg: ${theme.palette.dark.contrastText};
	}

	&.light {
		--panel-header-bg: ${theme.palette.background.paper};
		--panel-header-fg: ${theme.palette.text.primary};
	}

	.panel__header {
		display: grid;
		grid-template-areas: 'title content actions';
		grid-template-columns: auto 1fr auto;
		gap: ${theme.spacing(1)};
		min-height: ${theme.layout.targetSize};
		
		background-color: var(--panel-header-bg);
		color: var(--panel-header-fg);

		&-content {
			display: flex;
			align-items: center;
		}
	}

	.panel__content {
		position: relative;

		&.disabled {
			opacity: .6;
			pointer-events: none;
			user-select: none;
		}

		transition: opacity ${theme.transitions.duration.standard}ms;
		overflow-y: auto;
	}

	.panel__footer {
		background-color: ${theme.palette.background.paper};
		z-index: 1;
	}
`) as OverridableComponent<PaperTypeMap>;


export type PanelAction = ValueOrI18nKeyProp<"label"> & Partial<ValueOrI18nKeyProp<"tooltip">> & {
	icon?: IconComponent | undefined | null;
	color?: "primary" | "secondary" | "danger";
	disabled?: boolean;
	condition?: boolean | (() => boolean);
	submitFormId?: string | undefined | null;
	onClick?: VoidFunction | undefined | null;
};

export type PanelToolbarAction = Omit<PanelAction, "icon" | "label" | "labelKey"> & ValueOrI18nKeyProp<"label", string> & {
	icon: IconComponent;
	fullLabel?: boolean;
};

export type PanelProps = IdProp & ClassNameProp & PanelTitleProps & PanelToolbarProps & PanelActionsProps & ChildrenProp & {
	component?: keyof JSX.IntrinsicElements;
	headerContent?: JSX.Element | undefined | null | false;
	innerClassName?: string;
	loading?: boolean;
	variant?: "dark" | "light";
	elevation?: number;
	resource?: GqlResourceIdentifier | undefined | null;
	sx?: SxProps;
	innerSx?: SxProps;
};

export const Panel = forwardRef<HTMLElement, PanelProps>(({ component, id, disabled, loading, className, elevation, headerContent, innerClassName, variant = "dark", children, resource, sx, innerSx, ...props }, ref) => {
	return (
		<PanelRoot
			component={ component as any } // eslint-disable-line @typescript-eslint/no-explicit-any
			ref={ ref }
			id={ id }
			className={ classNames(className, variant) }
			elevation={ elevation ?? 1 }
			sx={ sx }
			{ ...resource && resourceProps(resource) }
		>
			<header className="panel__header">
				<PanelTitle { ...props }/>
				<div className="panel__header-content">
					{ headerContent }
				</div>
				<PanelToolbar { ...props } disabled={ disabled || loading }/>
			</header>
			<Box
				className={ classNames("panel__content", { disabled: disabled || loading }, innerClassName) }
				sx={ innerSx }
			>
				{ children }
			</Box>
			<PanelActions { ...props } className="panel__footer" disabled={ disabled || loading }/>
			{ loading && <CentredSpinner/> }
		</PanelRoot>
	);
});
